import React, { useEffect, useState } from "react";

import {
  AsyncSelect,
  Carousel,
  CheckForm,
  Group,
  InlineAlert,
  Section,
  Select,
  WithLabel,
} from "@samskip/frontend-components";
import {
  Control,
  DeepRequired,
  FieldErrorsImpl,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  TransferPlace,
  LabelConstants,
  BookingTemplateConstants,
} from "@assets/constants";
import { BookingTemplateFormTypes } from "@assets/formTypes";
import {
  TransportRequestForm,
  transferAddressFormFieldNames,
} from "@assets/formTypes/bookingTemplates";
import { useAppSelector } from "@assets/hooks/redux";
import { CommonTypes, PortTypes } from "@assets/types";
import {
  countryLabelMapper,
  countryValueMapper,
  portLabelMapper,
  portValueMapper,
  cityLabelMapper,
} from "@assets/utilities/labelMapperUtils";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import {
  WithFormController,
  TransportRequestModal,
  TransportRequestCarouselInfo,
} from "@components";
import PortService from "@services/PortService";
import { portsState } from "@state/slices/portsSlice";
import { countriesState } from "@state/slices/registrySlice";

type TransferAddressesSectionProps = {
  control: Control<BookingTemplateFormTypes.RouteForm>;
  errors: FieldErrorsImpl<DeepRequired<BookingTemplateFormTypes.RouteForm>>;
  onCityChange: CommonTypes.OneParamCallbackFunction<PortTypes.Port>;
  onCountryChange: CommonTypes.OneParamCallbackFunction<BookingTemplateConstants.TRANSFER_ADDRESS_TYPE>;
  onPlaceChange: CommonTypes.OneParamCallbackFunction<BookingTemplateConstants.TRANSFER_ADDRESS_TYPE>;
  onPortChange: CommonTypes.CallbackFunction;
  onTransportRequestChange: (
    transportRequests: TransportRequestForm[],
    transferAddressType: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => void;
  onTransportRequestClear: CommonTypes.OneParamCallbackFunction<BookingTemplateConstants.TRANSFER_ADDRESS_TYPE>;
  register: UseFormRegister<BookingTemplateFormTypes.RouteForm>;
  type: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE;
};

const TransferAddressesSection = ({
  control,
  errors,
  onCityChange,
  onCountryChange,
  onPlaceChange,
  onPortChange,
  onTransportRequestChange,
  onTransportRequestClear,
  register,
  type,
}: TransferAddressesSectionProps) => {
  const [foundCities, setFoundCities] = useState<PortTypes.Port[]>([]);
  const [openedModal, setOpenedModal] =
    useState<BookingTemplateConstants.TRANSFER_ADDRESS_TYPE | null>(null);

  const ports = useAppSelector(portsState);
  const allCountries = useAppSelector(countriesState);

  const registeredFields = transferAddressFormFieldNames[type];

  const { t } = useTranslation();

  const selectedCity: PortTypes.Port | null = useWatch({
    control,
    name: registeredFields.Point,
  });

  const selectedCountry: CommonTypes.Country | null = useWatch({
    control,
    name: registeredFields.Country,
  });

  const typeOfAddress: TransferPlace = useWatch({
    control,
    name: registeredFields.TransferType,
  });

  const shipperValues = useWatch({
    control,
    name: "Shipper",
  });

  const consigneeValues = useWatch({
    control,
    name: "Consignee",
  });

  const transportRequestValues: TransportRequestForm[] = useWatch({
    control,
    name: registeredFields.TransportRequest,
  });

  const openModal = (
    modalType: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE
  ) => {
    setOpenedModal(modalType);
  };

  const closeModal = () => {
    setOpenedModal(null);
  };

  const getTitle = (type: BookingTemplateConstants.TRANSFER_ADDRESS_TYPE) => {
    if (type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM)
      return t("LABEL_FROM");
    if (type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.TO)
      return t("LABEL_TO");
    return t("TEXT_NOT_PROVIDED");
  };

  const searchCity = (searchString: string): Promise<PortTypes.Port[]> => {
    // TODO: Review this. Temporary using this fix to deal with the issues that we are having with the useWatch not catching/updating when a new country has been selected and and when we want to search for a new city(Outside of the foundCities, but within the countryCode of the current selected country).
    // Normally we would just use the selectedCountry, but when we access this function the watch is not returning the right value.
    const searchCountryCode =
      control._formValues[registeredFields.Country].CountryCode;
    return PortService.searchPorts(searchString, searchCountryCode);
  };

  const handleSetCities = async (country: CommonTypes.Country | null) => {
    PortService.searchPorts(null, country?.CountryCode).then((portsByCountry) =>
      setFoundCities(portsByCountry)
    );
  };

  const handleOnCityChange = (port: PortTypes.Port) => {
    onCityChange(port);
    handleResetTransportRequest();
  };

  const handleResetTransportRequest = () => {
    onTransportRequestClear(type);
  };

  // Initialize the TransferAddressSection.
  // After one submit, make sure that the cities are still available from previous selection
  useEffect(() => {
    if (typeOfAddress === TransferPlace.ADDRESS) {
      if (selectedCountry !== null) {
        searchCity(selectedCountry.CountryCode).then((result) =>
          setFoundCities(result)
        );
      }
    }
  }, []);

  return (
    <Section id={type} title={getTitle(type)}>
      <Group onChange={() => onPlaceChange(type)} vertical>
        <CheckForm
          id={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? "place_from"
              : "place_to"
          }
          label={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? t("LABEL_PLACE_OF_RECEIPT")
              : t("LABEL_PLACE_OF_DELIVERY")
          }
          type="radio"
          value={TransferPlace.ADDRESS}
          {...register(registeredFields.TransferType)}
        />
        <CheckForm
          id={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? "port_from"
              : "port_to"
          }
          label={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? t("LABEL_SELF_DELIVER_PORT")
              : t("LABEL_SELF_PICKUP_PORT")
          }
          type="radio"
          value={TransferPlace.PORT}
          {...register(registeredFields.TransferType)}
        />
      </Group>
      {typeOfAddress === TransferPlace.ADDRESS && (
        <>
          <WithFormController control={control} name={registeredFields.Country}>
            <Select
              errorMessage={getErrorMessage(registeredFields.Country, errors)}
              fullWidth
              getOptionLabel={countryLabelMapper}
              getOptionValue={countryValueMapper}
              hasError={errors[registeredFields.Country] != null}
              id={`${type}-country`}
              isClearable
              label={t("LABEL_COUNTRY")}
              onChange={async (country: CommonTypes.Country | null) =>
                await handleSetCities(country).then(() => {
                  onCountryChange(type);
                })
              }
              options={allCountries}
              placeholder={t("PLACEHOLDER_SELECT_COUNTRY")}
              portaling
            />
          </WithFormController>
          <WithFormController control={control} name={registeredFields.Point}>
            <AsyncSelect
              defaultOptions={foundCities}
              defaultOptionsMessage={t(
                LabelConstants.SELECT_LABELS.DEFAULT_OPTIONS_MESSAGE
              )}
              disabled={
                !selectedCountry?.CountryCode &&
                typeOfAddress === TransferPlace.ADDRESS
              }
              disabledTooltipContent={t("ERROR_SELECT_COUNTRY_FIRST")}
              errorMessage={getErrorMessage(registeredFields.Point, errors)}
              fullWidth
              getOptionLabel={cityLabelMapper}
              getOptionValue={portValueMapper}
              hasError={errors[registeredFields.Point] != null}
              id={registeredFields.Point}
              isClearable
              label={t("LABEL_CITY")}
              loadOptions={searchCity}
              noOptionsMessage={t(LabelConstants.SELECT_LABELS.NO_OPTIONS)}
              onChange={handleOnCityChange}
              placeholder={t("PLACEHOLDER_CITY")}
            />
          </WithFormController>
          {selectedCountry?.CountryCode === "IS" ? (
            <InlineAlert>
              {type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
                ? t("INFO_TRANSPORT_REQUEST_FROM")
                : t("INFO_TRANSPORT_REQUEST_TO")}
            </InlineAlert>
          ) : (
            selectedCity && (
              <WithLabel label={t("LABEL_TRANSPORT_REQUEST")}>
                <Carousel
                  actionText={t("LABEL_EDIT")}
                  data={transportRequestValues}
                  formatContent={(content) => (
                    <TransportRequestCarouselInfo content={content} />
                  )}
                  formatStopsText={(currentPage: number, totalPage: number) =>
                    t("LABEL_N_OF_M_STOPS", {
                      n: currentPage,
                      m: totalPage,
                    })
                  }
                  isRemovable
                  noDataActionText={t("LABEL_ADD")}
                  noDataMessage={t("TEXT_NOT_PROVIDED")}
                  onActionClick={() => openModal(type)}
                  onNoDataActionClick={() => openModal(type)}
                  onRemoveClick={handleResetTransportRequest}
                  removeText={t("LABEL_REMOVE")}
                />
              </WithLabel>
            )
          )}
        </>
      )}
      <WithFormController control={control} name={registeredFields.Port}>
        <Select
          errorMessage={getErrorMessage(registeredFields.Port, errors)}
          fullWidth
          getOptionLabel={portLabelMapper}
          getOptionValue={portValueMapper}
          hasError={errors[registeredFields.Port] != null}
          id={`${type}-port`}
          isClearable
          label={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? t("LABEL_PORT_OF_LOAD")
              : t("LABEL_PORT_OF_DISCHARGE")
          }
          onChange={onPortChange}
          options={ports}
          placeholder={t("PLACEHOLDER_SELECT_PORT")}
        />
      </WithFormController>
      {!!openedModal && (
        <TransportRequestModal
          formValues={transportRequestValues}
          onClose={closeModal}
          onSave={onTransportRequestChange}
          partnerValues={
            type === BookingTemplateConstants.TRANSFER_ADDRESS_TYPE.FROM
              ? shipperValues
              : consigneeValues
          }
          title={t("LABEL_TRANSPORT_REQUEST")}
          type={type}
        />
      )}
    </Section>
  );
};

export default TransferAddressesSection;
